import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { 
  Clock, Calendar, Plus, X, Save, Loader2, RefreshCw,
  Sun, Moon, AlertCircle, CheckCircle, Users, Settings,
  ChevronDown, ChevronUp, Trash2, CalendarX
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverAvailability = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [newSlot, setNewSlot] = useState({ start: '09:00', end: '12:00' });
  const [blockDates, setBlockDates] = useState({ dates: '', reason: '' });
  const [showBlockDates, setShowBlockDates] = useState(false);
  
  const [availability, setAvailability] = useState({
    weekly_schedule: {
      monday: { enabled: true, slots: [] },
      tuesday: { enabled: true, slots: [] },
      wednesday: { enabled: true, slots: [] },
      thursday: { enabled: true, slots: [] },
      friday: { enabled: true, slots: [] },
      saturday: { enabled: false, slots: [] },
      sunday: { enabled: false, slots: [] }
    },
    timezone: 'Asia/Kolkata',
    max_sessions_per_day: 8,
    max_sessions_per_week: 40,
    session_duration_minutes: 15,
    break_between_sessions_minutes: 5,
    preferred_age_groups: [],
    preferred_languages: [],
    unavailable_dates: [],
    is_accepting_new_children: true
  });

  const days = [
    { key: 'monday', label: 'Monday', short: 'Mon' },
    { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
    { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
    { key: 'thursday', label: 'Thursday', short: 'Thu' },
    { key: 'friday', label: 'Friday', short: 'Fri' },
    { key: 'saturday', label: 'Saturday', short: 'Sat' },
    { key: 'sunday', label: 'Sunday', short: 'Sun' }
  ];

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('observer_token');
      const [availRes, summaryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/observer/availability`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/observer/availability/summary`, { params: { token } })
      ]);
      
      if (availRes.data) {
        setAvailability(availRes.data);
      }
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('observer_token');
      const schedule = availability.weekly_schedule;
      
      await axios.post(`${BACKEND_URL}/api/observer/availability`, null, {
        params: {
          token,
          monday_enabled: schedule.monday.enabled,
          monday_slots: schedule.monday.slots.join(','),
          tuesday_enabled: schedule.tuesday.enabled,
          tuesday_slots: schedule.tuesday.slots.join(','),
          wednesday_enabled: schedule.wednesday.enabled,
          wednesday_slots: schedule.wednesday.slots.join(','),
          thursday_enabled: schedule.thursday.enabled,
          thursday_slots: schedule.thursday.slots.join(','),
          friday_enabled: schedule.friday.enabled,
          friday_slots: schedule.friday.slots.join(','),
          saturday_enabled: schedule.saturday.enabled,
          saturday_slots: schedule.saturday.slots.join(','),
          sunday_enabled: schedule.sunday.enabled,
          sunday_slots: schedule.sunday.slots.join(','),
          timezone: availability.timezone,
          max_sessions_per_day: availability.max_sessions_per_day,
          max_sessions_per_week: availability.max_sessions_per_week,
          session_duration_minutes: availability.session_duration_minutes,
          break_between_sessions_minutes: availability.break_between_sessions_minutes,
          preferred_age_groups: availability.preferred_age_groups.join(','),
          preferred_languages: availability.preferred_languages.join(','),
          is_accepting_new_children: availability.is_accepting_new_children
        }
      });
      
      await loadData();
      alert('Availability saved successfully!');
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    setAvailability(prev => ({
      ...prev,
      weekly_schedule: {
        ...prev.weekly_schedule,
        [day]: {
          ...prev.weekly_schedule[day],
          enabled: !prev.weekly_schedule[day].enabled
        }
      }
    }));
  };

  const addSlot = (day) => {
    const slot = `${newSlot.start}-${newSlot.end}`;
    const currentSlots = availability.weekly_schedule[day].slots;
    
    if (!currentSlots.includes(slot)) {
      setAvailability(prev => ({
        ...prev,
        weekly_schedule: {
          ...prev.weekly_schedule,
          [day]: {
            ...prev.weekly_schedule[day],
            slots: [...prev.weekly_schedule[day].slots, slot]
          }
        }
      }));
    }
    setNewSlot({ start: '09:00', end: '12:00' });
  };

  const removeSlot = (day, slot) => {
    setAvailability(prev => ({
      ...prev,
      weekly_schedule: {
        ...prev.weekly_schedule,
        [day]: {
          ...prev.weekly_schedule[day],
          slots: prev.weekly_schedule[day].slots.filter(s => s !== slot)
        }
      }
    }));
  };

  const handleBlockDates = async () => {
    if (!blockDates.dates.trim()) {
      alert('Please enter at least one date');
      return;
    }
    
    try {
      const token = localStorage.getItem('observer_token');
      await axios.post(`${BACKEND_URL}/api/observer/availability/block-dates`, null, {
        params: {
          token,
          dates: blockDates.dates,
          reason: blockDates.reason
        }
      });
      
      setBlockDates({ dates: '', reason: '' });
      setShowBlockDates(false);
      loadData();
      alert('Dates blocked successfully!');
    } catch (error) {
      console.error('Error blocking dates:', error);
      alert('Failed to block dates');
    }
  };

  const unblockDate = async (date) => {
    try {
      const token = localStorage.getItem('observer_token');
      await axios.delete(`${BACKEND_URL}/api/observer/availability/unblock-date`, {
        params: { token, date }
      });
      loadData();
    } catch (error) {
      console.error('Error unblocking date:', error);
    }
  };

  const getCapacityColor = (status) => {
    switch (status) {
      case 'full': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'moderate': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Clock className="w-7 h-7 text-violet-600" />
                Availability Settings
              </h1>
              <p className="text-gray-600">Set your schedule and capacity preferences</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button 
                onClick={saveAvailability}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                )}
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Hours/Week</p>
                      <p className="text-2xl font-bold text-violet-600">{summary.total_hours_per_week}</p>
                    </div>
                    <Clock className="w-8 h-8 text-violet-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Days Available</p>
                      <p className="text-2xl font-bold text-blue-600">{summary.days_available}/7</p>
                    </div>
                    <Calendar className="w-8 h-8 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Assigned Children</p>
                      <p className="text-2xl font-bold text-green-600">{summary.current_assigned_children}</p>
                    </div>
                    <Users className="w-8 h-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Capacity</p>
                      <p className={`text-lg font-bold px-2 py-0.5 rounded ${getCapacityColor(summary.capacity_status)}`}>
                        {summary.capacity_percent}%
                      </p>
                    </div>
                    <Settings className="w-8 h-8 text-gray-200" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Accepting New Children Toggle */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    availability?.is_accepting_new_children ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {availability?.is_accepting_new_children ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Accepting New Children</p>
                    <p className="text-sm text-gray-500">
                      {availability?.is_accepting_new_children 
                        ? 'You can be assigned new children' 
                        : 'You will not receive new assignments'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={availability?.is_accepting_new_children || false}
                  onCheckedChange={(checked) => setAvailability(prev => ({
                    ...prev,
                    is_accepting_new_children: checked
                  }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Weekly Schedule */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-violet-600" />
                Weekly Schedule
              </h2>
              
              <div className="space-y-3">
                {days.map(({ key, label }) => {
                  const dayData = availability.weekly_schedule?.[key] || { enabled: false, slots: [] };
                  const isExpanded = expandedDay === key;
                  const isWeekend = key === 'saturday' || key === 'sunday';
                  
                  return (
                    <div 
                      key={key}
                      className={`border rounded-lg overflow-hidden transition-all ${
                        dayData.enabled ? 'border-violet-200' : 'border-gray-200'
                      }`}
                    >
                      {/* Day Header */}
                      <div 
                        className={`px-4 py-3 flex items-center justify-between cursor-pointer ${
                          dayData.enabled ? 'bg-violet-50' : 'bg-gray-50'
                        }`}
                        onClick={() => setExpandedDay(isExpanded ? null : key)}
                      >
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={dayData.enabled}
                            onCheckedChange={() => toggleDay(key)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex items-center gap-2">
                            {isWeekend ? (
                              <Moon className="w-4 h-4 text-gray-400" />
                            ) : (
                              <Sun className="w-4 h-4 text-yellow-500" />
                            )}
                            <span className={`font-medium ${dayData.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                              {label}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {dayData.slots.length > 0 && (
                            <span className="text-sm text-gray-500">
                              {dayData.slots.length} slot(s)
                            </span>
                          )}
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Content */}
                      {isExpanded && dayData.enabled && (
                        <div className="px-4 py-4 bg-white border-t">
                          {/* Current Slots */}
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Time Slots</p>
                            {dayData.slots.length === 0 ? (
                              <p className="text-sm text-gray-400 italic">No slots added yet</p>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {dayData.slots.map((slot, idx) => (
                                  <div 
                                    key={idx}
                                    className="flex items-center gap-2 bg-violet-100 text-violet-700 px-3 py-1 rounded-full"
                                  >
                                    <Clock className="w-3 h-3" />
                                    <span className="text-sm font-medium">{slot}</span>
                                    <button
                                      onClick={() => removeSlot(key, slot)}
                                      className="hover:text-red-600 transition-colors"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Add New Slot */}
                          <div className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={newSlot.start}
                              onChange={(e) => setNewSlot(prev => ({ ...prev, start: e.target.value }))}
                              className="w-32"
                            />
                            <span className="text-gray-500">to</span>
                            <Input
                              type="time"
                              value={newSlot.end}
                              onChange={(e) => setNewSlot(prev => ({ ...prev, end: e.target.value }))}
                              className="w-32"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addSlot(key)}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Slot
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Capacity Settings */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-violet-600" />
                Capacity Settings
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Sessions Per Day
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={availability?.max_sessions_per_day || 8}
                    onChange={(e) => setAvailability(prev => ({
                      ...prev,
                      max_sessions_per_day: parseInt(e.target.value) || 8
                    }))}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Sessions Per Week
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={availability?.max_sessions_per_week || 40}
                    onChange={(e) => setAvailability(prev => ({
                      ...prev,
                      max_sessions_per_week: parseInt(e.target.value) || 40
                    }))}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session Duration (minutes)
                  </label>
                  <Input
                    type="number"
                    min="5"
                    max="60"
                    value={availability?.session_duration_minutes || 15}
                    onChange={(e) => setAvailability(prev => ({
                      ...prev,
                      session_duration_minutes: parseInt(e.target.value) || 15
                    }))}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Break Between Sessions (minutes)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={availability?.break_between_sessions_minutes || 5}
                    onChange={(e) => setAvailability(prev => ({
                      ...prev,
                      break_between_sessions_minutes: parseInt(e.target.value) || 5
                    }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Block Dates */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CalendarX className="w-5 h-5 text-red-500" />
                  Blocked Dates
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBlockDates(!showBlockDates)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Block Dates
                </Button>
              </div>
              
              {showBlockDates && (
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dates (comma-separated, e.g., 2024-01-15, 2024-01-16)
                      </label>
                      <Input
                        value={blockDates.dates}
                        onChange={(e) => setBlockDates(prev => ({ ...prev, dates: e.target.value }))}
                        placeholder="YYYY-MM-DD, YYYY-MM-DD"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason (optional)
                      </label>
                      <Input
                        value={blockDates.reason}
                        onChange={(e) => setBlockDates(prev => ({ ...prev, reason: e.target.value }))}
                        placeholder="e.g., Vacation, Personal leave"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleBlockDates} className="bg-red-600 hover:bg-red-700 text-white">
                        Block Dates
                      </Button>
                      <Button variant="outline" onClick={() => setShowBlockDates(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {availability.unavailable_dates && availability.unavailable_dates.length > 0 ? (
                <div className="space-y-2">
                  {availability.unavailable_dates.map((blocked, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between bg-red-50 border border-red-100 px-4 py-2 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <CalendarX className="w-4 h-4 text-red-500" />
                        <span className="font-medium text-gray-900">{blocked.date}</span>
                        {blocked.reason && (
                          <span className="text-sm text-gray-500">— {blocked.reason}</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unblockDate(blocked.date)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic">No blocked dates</p>
              )}
            </CardContent>
          </Card>

          {/* Save Button (Bottom) */}
          <div className="flex justify-end">
            <Button 
              onClick={saveAvailability}
              disabled={saving}
              size="lg"
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save All Changes</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObserverAvailability;
